import React, { useMemo, useState } from 'react'
import { Box, Button, Chip, Divider, Grid, LinearProgress, Paper, Rating, Stack, Typography } from '@mui/material'
import { LocalShipping, LocationOn, People } from '@mui/icons-material'
import Groups from '../views/Groups'
import { SearchHeader } from '../common'
import { CreateTransporterDialog, RepairShopChecklistDialog } from '../dialogs'
import { RepairShopLocation, Transporter, TransporterGroup } from '@otw/models'
import { useRepairShopLocations } from 'hooks/inventory/useRepairShopLocations'
import { useLogistics } from '@/hooks/logistics/useLogistics'
import { useInspectionChecklist } from '@/hooks/repair/useInspectionChecklist'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { InspectionChecklistQuestion } from '@otw/models'
import { normalizeRepairUserShopIds, type RepairUserRecord } from '@/hooks/admin/useRepairUsers'

interface GroupsTabProps {
  transporterGroups: TransporterGroup[]
  transporters: Transporter[]
  repairUsers: RepairUserRecord[]
  onCreateTransporter: (transporterData: {
    name: string
    driver_ids: string[]
    admin_ids: string[]
    owner_id: string
  }) => Promise<void>
  onEditGroup: (group: TransporterGroup) => void
  onDeleteGroup: (groupId: string) => void
  onOpenTemplateConfig?: (companyId: string) => void
}

const GroupsTab: React.FC<GroupsTabProps> = ({
  transporterGroups,
  transporters,
  repairUsers,
  onCreateTransporter,
  onEditGroup,
  onDeleteGroup,
  onOpenTemplateConfig
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isTransportView, setIsTransportView] = useState(true)
  const [isCreateGroupDialogOpen, setIsCreateGroupDialogOpen] = useState(false)
  const [selectedRepairShop, setSelectedRepairShop] = useState<RepairShopLocation | null>(null)
  const [checklistDialogKind, setChecklistDialogKind] = useState<'intake' | 'complete'>('complete')
  const [isChecklistDialogOpen, setIsChecklistDialogOpen] = useState(false)
  const { showSuccess, showError } = useSnackbar()
  const { repairShopLocations, updateRepairShopLocation } = useRepairShopLocations()
  const {
    transportTrucks,
    repairShopAvgDropoffToCompleteMsById,
    transporterGroupAvgReadyToInProgressMsById,
  } = useLogistics()

  const selectedChecklistId = useMemo(() => {
    if (!selectedRepairShop?.id) return undefined
    if (checklistDialogKind === 'intake') {
      return selectedRepairShop.intake_checklist_id || `repair-shop-${selectedRepairShop.id}-intake`
    }
    return selectedRepairShop.inspection_checklist_id || `repair-shop-${selectedRepairShop.id}`
  }, [selectedRepairShop, checklistDialogKind])

  const { saveChecklist: saveShopChecklist, isSaving: isSavingShopChecklist } = useInspectionChecklist({
    checklistId: selectedChecklistId,
    kind: checklistDialogKind,
    fallbackToDefault: false,
  })

  const handleCreateGroup = () => {
    setIsCreateGroupDialogOpen(true)
  }

  const handleCloseCreateGroup = () => {
    setIsCreateGroupDialogOpen(false)
  }

  const handleCreateGroupSubmit = async (transporterData: {
    name: string
    driver_ids: string[]
    admin_ids: string[]
    owner_id: string
    price_per_mile?: number
  }) => {
    try {
      await onCreateTransporter(transporterData)
      setIsCreateGroupDialogOpen(false)
    } catch (error) {
      console.error('Error creating group:', error)
    }
  }

  const handleOpenChecklistDialog = (shop: RepairShopLocation, kind: 'intake' | 'complete') => {
    setSelectedRepairShop(shop)
    setChecklistDialogKind(kind)
    setIsChecklistDialogOpen(true)
  }

  const handleCloseChecklistDialog = () => {
    setIsChecklistDialogOpen(false)
    setSelectedRepairShop(null)
  }

  const handleSaveShopChecklist = async (questions: InspectionChecklistQuestion[]) => {
    if (!selectedRepairShop?.id) {
      showError('Repair shop not found')
      return
    }
    const isIntake = checklistDialogKind === 'intake'
    const checklistId = isIntake
      ? selectedRepairShop.intake_checklist_id || `repair-shop-${selectedRepairShop.id}-intake`
      : selectedRepairShop.inspection_checklist_id || `repair-shop-${selectedRepairShop.id}`
    const shopLabel = selectedRepairShop.name || 'Repair Shop'
    const kindLabel = isIntake ? 'Intake' : 'Complete'

    try {
      await saveShopChecklist({
        name: `${shopLabel} ${kindLabel} Checklist`,
        questions,
        kind: checklistDialogKind,
      })
      await updateRepairShopLocation({
        ...selectedRepairShop,
        shop_type: selectedRepairShop.shop_type || [],
        ...(isIntake
          ? { intake_checklist_id: checklistId }
          : { inspection_checklist_id: checklistId }),
      })
      showSuccess(`${kindLabel} checklist assigned to repair shop`)
      handleCloseChecklistDialog()
    } catch (error) {
      console.error(`Error assigning ${kindLabel.toLowerCase()} checklist to repair shop:`, error)
      showError(`Failed to assign ${kindLabel.toLowerCase()} checklist`)
    }
  }

  const normalizedSearch = searchTerm.trim().toLowerCase()
  const trucksAtShopCountById = useMemo(() => {
    return transportTrucks.reduce<Record<string, number>>((acc, truck) => {
      if (!truck.currentLocationId) return acc
      acc[truck.currentLocationId] = (acc[truck.currentLocationId] ?? 0) + 1
      return acc
    }, {})
  }, [transportTrucks])

  const repairShopsWithUsers = ((repairShopLocations || []) as RepairShopLocation[])
    .filter((shop): shop is RepairShopLocation & { id: string } => typeof shop.id === 'string' && shop.id.length > 0)
    .map((shop) => {
      const usersForShop = repairUsers.filter((user) =>
        normalizeRepairUserShopIds(user).includes(shop.id)
      )
      return {
        shop,
        users: usersForShop,
      }
    })
    .filter(({ users }) => users.length > 0)
    .filter(({ shop, users }) => {
      if (!normalizedSearch) return true

      const searchableValues = [
        shop.name,
        shop.address,
        shop.city,
        shop.state,
        shop.zip,
        ...users.flatMap((user) => [user.name, user.email, user.phone]),
      ]
        .filter(Boolean)
        .map((value) => value!.toLowerCase())

      return searchableValues.some((value) => value.includes(normalizedSearch))
    })

  const formatAverageDuration = (durationMs?: number) => {
    if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) {
      return 'N/A'
    }
    const totalMinutes = Math.round(durationMs / (1000 * 60))
    const days = Math.floor(totalMinutes / (60 * 24))
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
    const minutes = totalMinutes % 60
    if (days > 0) {
      return `${days}d ${hours}h`
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <SearchHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search companies..."
        showTransportInventoryToggle={true}
        isTransportView={isTransportView}
        onTransportInventoryToggle={setIsTransportView}
        transportInventoryToggleLabels={{ left: 'Transport', right: 'Repair' }}
        showCreateButton={isTransportView}
        createButtonText="New Company"
        onCreateClick={isTransportView ? handleCreateGroup : undefined}
        showDashboard={false}
      />
      
      {isTransportView ? (
        <Groups
          transporterGroups={transporterGroups}
          transporters={transporters}
          searchTerm={searchTerm}
          transporterGroupAvgReadyToInProgressMsById={transporterGroupAvgReadyToInProgressMsById}
          onEditGroup={onEditGroup}
          onDeleteGroup={onDeleteGroup}
          onOpenTemplateConfig={onOpenTemplateConfig}
        />
      ) : (
        repairShopsWithUsers.length > 0 ? (
          <Grid container spacing={2}>
            {repairShopsWithUsers.map(({ shop, users }) => {
              const addressText = [shop.address, shop.city, shop.state, shop.zip].filter(Boolean).join(', ')
              const trucksAtShop = shop.id ? (trucksAtShopCountById[shop.id] ?? 0) : 0
              const capacity = typeof shop.capacity === 'number' && Number.isFinite(shop.capacity) && shop.capacity > 0
                ? shop.capacity
                : null
              const capacityPercent = capacity ? Math.min((trucksAtShop / capacity) * 100, 100) : 0
              return (
                <Grid item xs={12} sm={6} md={4} key={shop.id || addressText}>
                  <Paper
                    sx={{
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      '&:hover': { boxShadow: 3 },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant='h6' component='div'>
                            {shop.name || 'Unnamed Shop'}
                          </Typography>
                          <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 0.5 }}>
                            <LocationOn sx={{ color: 'text.secondary', mt: 0.1, fontSize: '1rem' }} />
                            <Typography variant="body2" color="text.secondary">
                              {addressText || 'No address on file'}
                            </Typography>
                          </Stack>
                        </Box>

                        <Chip
                          size="small"
                          icon={<LocalShipping sx={{ fontSize: '0.9rem !important' }} />}
                          label={`${trucksAtShop} truck${trucksAtShop === 1 ? '' : 's'}`}
                          color={trucksAtShop > 0 ? 'primary' : 'default'}
                          variant={trucksAtShop > 0 ? 'filled' : 'outlined'}
                        />
                      </Box>

                      <Divider />

                      <Box>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                          <People sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                          <Typography variant="subtitle2">Users</Typography>
                        </Stack>
                        <Stack
                          spacing={1}
                          sx={{
                            mt: 1,
                            maxHeight: 150,
                            overflowY: 'auto',
                            '& > div': {
                              p: 1,
                              borderRadius: 1,
                              bgcolor: 'background.default',
                              borderLeft: '3px solid',
                              borderColor: 'secondary.main',
                            },
                          }}
                        >
                          {users.map((user) => (
                            <Box key={user.id}>
                              <Typography variant="body2">
                                {user.name || user.email || user.phone || user.id}
                              </Typography>
                              {(user.email || user.phone) && (
                                <Typography variant="caption" color="text.secondary">
                                  {user.email || user.phone}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                          Metrics
                        </Typography>
                        <Box sx={{ mb: 1.25 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Capacity usage
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {capacity ? `${trucksAtShop} / ${capacity}` : `${trucksAtShop} / N/A`}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={capacityPercent}
                            color={capacity && trucksAtShop >= capacity ? 'warning' : 'primary'}
                            sx={{ height: 8, borderRadius: 999 }}
                          />
                        </Box>
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">Price</Typography>
                            <Rating size="small" precision={0.5} value={shop.price ?? 0} readOnly />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">Communication</Typography>
                            <Rating size="small" precision={0.5} value={shop.communication ?? 0} readOnly />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">Speed</Typography>
                            <Rating size="small" precision={0.5} value={shop.speed ?? 0} readOnly />
                          </Box>
                        </Stack>
                      </Box>

                      <Box sx={{ mt: 'auto' }}>
                        <Box
                          sx={{
                            mt: 2,
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'secondary.light',
                            background: 'linear-gradient(135deg, rgba(156,39,176,0.14) 0%, rgba(156,39,176,0.06) 100%)',
                            textAlign: 'center',
                            boxShadow: '0 4px 12px rgba(156,39,176,0.12)',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: 'secondary.dark', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}
                          >
                            Repair time
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.6 }}>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.35,
                                borderRadius: 999,
                                bgcolor: 'rgba(156,39,176,0.18)',
                                border: '1px solid rgba(156,39,176,0.3)',
                              }}
                            >
                              <Typography variant="h6" sx={{ color: 'secondary.dark', lineHeight: 1.1, fontWeight: 700 }}>
                                {formatAverageDuration(shop.id ? repairShopAvgDropoffToCompleteMsById[shop.id] : undefined)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenChecklistDialog(shop, 'intake')}
                            >
                              {shop.intake_checklist_id ? 'Edit intake checklist' : 'Create intake checklist'}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleOpenChecklistDialog(shop, 'complete')}
                            >
                              {shop.inspection_checklist_id
                                ? 'Edit complete checklist'
                                : 'Create complete checklist'}
                            </Button>
                          </Stack>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              )
            })}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? 'No repair shops found matching your search' : 'No repair shops with assigned users yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Assign repair users to shops to populate this view'}
            </Typography>
          </Box>
        )
      )}
      
      {/* Create Group Dialog */}
      <CreateTransporterDialog
        open={isCreateGroupDialogOpen}
        onClose={handleCloseCreateGroup}
        onSubmit={handleCreateGroupSubmit}
        transporters={transporters}
      />

      <RepairShopChecklistDialog
        open={isChecklistDialogOpen}
        shopName={selectedRepairShop?.name}
        checklistId={selectedChecklistId}
        checklistKind={checklistDialogKind}
        checklistKindLabel={checklistDialogKind === 'intake' ? 'Intake' : 'Complete'}
        isSaving={isSavingShopChecklist}
        onClose={handleCloseChecklistDialog}
        onSave={handleSaveShopChecklist}
      />
    </Stack>
  )
}

export default GroupsTab
